import React from 'react';
import styled, {createGlobalStyle} from 'styled-components';
import {useIntl} from 'react-intl';
import {Button} from '@forecast-it/design-system';
import {SaveState, isSaveDisabled} from '../shared/saveState';
import {SaveStatePill} from './SaveStatePill';
import {NovaStudioColor} from '../shared/novaStudioColors';

const HeaderRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 16px 24px;
	border-bottom: 1px solid ${NovaStudioColor.border};
`;
const AgentName = styled.h1`
	font-size: 20px;
	margin: 0;
`;
const HeaderActions = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
`;

const SAVE_BUTTON_TEST_ID = 'nova-save';
const HISTORY_BUTTON_TEST_ID = 'nova-open-history';

// The Save button is intentionally never natively `disabled`: the guard that makes it a no-op
// while the draft is clean or a save is already in flight lives in the `onSave` handler the shell
// hands down (see NovaStudioShell.tsx), not here, so the same guard also protects the ⌘S/Ctrl+S
// shortcut. This restores a minimal equivalent look for its `aria-disabled` state, mirroring
// CreateAgentModal.tsx's submit button.
const SaveButtonAriaDisabledStyle = createGlobalStyle`
	[data-cy="${SAVE_BUTTON_TEST_ID}"][aria-disabled='true'] {
		opacity: 0.5;
		cursor: default;
	}
`;

interface Props {
	agentName: string | null;
	saveState: SaveState;
	// Whether the working draft has unsaved changes — combined with `saveState` to decide whether
	// the Save button is a no-op (see `isSaveDisabled`).
	isDirty: boolean;
	// Whether the selected agent's real content has actually finished loading into the draft (see
	// NovaStudioPage.tsx's detail-load gate) — combined with `isDirty`/`saveState` in
	// `isSaveDisabled` so Save can never fire while the draft still only holds empty/stale
	// placeholder values. Defaults to true so callers that don't exercise this gate (tests, and
	// any future caller with nothing to gate on) keep the old dirty/in-flight-only behavior.
	canSave?: boolean;
	// Triggers a save of the current working draft. Already guarded by the caller against a no-op
	// (clean draft) or a duplicate in-flight save, so this can be called unconditionally here.
	onSave: () => void;
	// Opens the version-history modal for the selected agent — owned by NovaStudioShell (which
	// has the agent's versions/currentVersionNumber and the restore handler), mirroring how
	// `onSave` is a plain, already-wired callback here rather than this component reaching for
	// Relay/modal state itself.
	onOpenHistory: () => void;
}

export function NovaStudioHeader({agentName, saveState, isDirty, canSave = true, onSave, onOpenHistory}: Props): JSX.Element {
	const {formatMessage} = useIntl();

	return (
		<HeaderRow data-cy="nova-studio-header">
			<SaveButtonAriaDisabledStyle />
			{/* Placeholder copy — not localised yet; must go through the translations workflow. */}
			<AgentName>{agentName ?? 'No agent selected'}</AgentName>
			{agentName !== null && (
				<HeaderActions>
					<SaveStatePill saveState={saveState} />
					<Button data-cy={HISTORY_BUTTON_TEST_ID} emphasis="low" onPress={onOpenHistory}>
						{formatMessage({id: 'nova.studio.historyButton'})}
					</Button>
					<Button
						data-cy={SAVE_BUTTON_TEST_ID}
						aria-disabled={isSaveDisabled(isDirty, saveState, canSave)}
						onPress={onSave}
					>
						{formatMessage({id: 'nova.studio.saveButton'})}
					</Button>
				</HeaderActions>
			)}
		</HeaderRow>
	);
}
